import React, { useState, useRef, useEffect, useCallback } from 'react';
import styles from '../../styles/interactive-docs.module.css';
import SandboxWrapper from './SandboxWrapper';
import {
    RulerIcon,
    DrawPolygonIcon,
    CrosshairIcon,
    CopyIcon
} from '../../../../components/Icon';

const SAMPLE_MAP_OBJECTS = [
    {
        id: 'plot-101',
        type: 'plot',
        name: 'Участок №101',
        sublabel: '90 × 110 м (99 сот.)',
        points: [
            { x: 40, y: 50 },
            { x: 220, y: 50 },
            { x: 220, y: 270 },
            { x: 40, y: 270 }
        ],
        fill: 'rgba(59, 130, 246, 0.08)',
        stroke: '#3b82f6'
    },
    {
        id: 'building-1',
        type: 'building',
        name: 'Коттедж №1',
        sublabel: '50 × 40 м (2 этажа)',
        points: [
            { x: 80, y: 90 },
            { x: 180, y: 90 },
            { x: 180, y: 170 },
            { x: 80, y: 170 }
        ],
        fill: 'rgba(245, 158, 11, 0.22)',
        stroke: '#f59e0b'
    },
    {
        id: 'plot-102',
        type: 'plot',
        name: 'Участок №102',
        sublabel: 'В продаже (99 сот.)',
        points: [
            { x: 250, y: 50 },
            { x: 430, y: 50 },
            { x: 430, y: 270 },
            { x: 250, y: 270 }
        ],
        fill: 'rgba(16, 185, 129, 0.08)',
        stroke: '#10b981'
    },
    {
        id: 'building-2',
        type: 'building',
        name: 'Фундамент',
        sublabel: '50 × 35 м',
        points: [
            { x: 290, y: 100 },
            { x: 390, y: 100 },
            { x: 390, y: 170 },
            { x: 290, y: 170 }
        ],
        fill: 'rgba(139, 92, 246, 0.22)',
        stroke: '#8b5cf6'
    },
    {
        id: 'plot-103',
        type: 'plot',
        name: 'Участок №103',
        sublabel: 'Свободный лот',
        points: [
            { x: 460, y: 50 },
            { x: 640, y: 50 },
            { x: 640, y: 270 },
            { x: 460, y: 270 }
        ],
        fill: 'rgba(14, 165, 233, 0.08)',
        stroke: '#0ea5e9'
    }
];

// Масштаб: 1 пиксель = 0.5 метра
const SCALE_METERS_PER_PX = 0.5;

// Расчёт расстояния между двумя точками
const getDistanceMeters = (p1, p2) => {
    const px = Math.hypot(p2.x - p1.x, p2.y - p1.y);
    return Math.round(px * SCALE_METERS_PER_PX * 10) / 10;
};

// Расчёт площади полигона (формула Гаусса)
const getPolygonAreaMeters = (points) => {
    if (points.length < 3) return { sqMeters: 0, sotki: 0, perimeter: 0, segments: [] };
    let area = 0;
    let perimeter = 0;
    const segments = [];
    for (let i = 0; i < points.length; i++) {
        const j = (i + 1) % points.length;
        area += points[i].x * points[j].y;
        area -= points[j].x * points[i].y;
        const segDist = getDistanceMeters(points[i], points[j]);
        perimeter += segDist;
        segments.push({
            p1: points[i],
            p2: points[j],
            dist: segDist,
            midX: (points[i].x + points[j].x) / 2,
            midY: (points[i].y + points[j].y) / 2
        });
    }
    const sqPx = Math.abs(area) / 2;
    const sqMeters = Math.round(sqPx * (SCALE_METERS_PER_PX * SCALE_METERS_PER_PX));
    const sotki = (sqMeters / 100).toFixed(1);
    return { sqMeters, sotki, perimeter: Math.round(perimeter * 10) / 10, segments };
};

// Проверка попадания точки в полигон
const isPointInPolygon = (pt, poly) => {
    let inside = false;
    for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
        const xi = poly[i].x, yi = poly[i].y;
        const xj = poly[j].x, yj = poly[j].y;
        const intersect = ((yi > pt.y) !== (yj > pt.y)) &&
            (pt.x < (xj - xi) * (pt.y - yi) / (yj - yi) + xi);
        if (intersect) inside = !inside;
    }
    return inside;
};

// Функция привязки к углам 45° и 90° при Shift
const getSnappedCoordinates = (rawX, rawY, points, isShift) => {
    if (!isShift || points.length === 0) return { x: rawX, y: rawY };
    const prevPt = points[points.length - 1];
    const dx = rawX - prevPt.x;
    const dy = rawY - prevPt.y;
    const dist = Math.hypot(dx, dy);
    if (dist === 0) return { x: rawX, y: rawY };
    const angleRad = Math.atan2(dy, dx);
    const angleDeg = (angleRad * 180) / Math.PI;
    const snappedAngleDeg = Math.round(angleDeg / 45) * 45;
    const rad = (snappedAngleDeg * Math.PI) / 180;
    return {
        x: Math.round(prevPt.x + dist * Math.cos(rad)),
        y: Math.round(prevPt.y + dist * Math.sin(rad))
    };
};

const MeasurementSandbox = () => {
    const [activeTool, setActiveTool] = useState('ruler'); // 'ruler' | 'area' | 'coords' | null
    const [currentPoints, setCurrentPoints] = useState([]);
    const [cursorPos, setCursorPos] = useState(null);
    const [isShiftPressed, setIsShiftPressed] = useState(false);
    const [completedMeasurements, setCompletedMeasurements] = useState([]);
    const [selectedCoords, setSelectedCoords] = useState(null);
    const [copiedToast, setCopiedToast] = useState(false);
    const svgRef = useRef(null);

    // Переключение инструмента: при клике на любой инструмент поле очищается для нового замера
    const handleToolSelect = (toolId) => {
        setCompletedMeasurements([]);
        setCurrentPoints([]);
        setSelectedCoords(null);
        setActiveTool(toolId);
    };

    const finishMeasurement = useCallback(() => {
        if (activeTool === 'ruler' && currentPoints.length >= 2) {
            let total = 0;
            const segments = [];
            for (let i = 0; i < currentPoints.length - 1; i++) {
                const segDist = getDistanceMeters(currentPoints[i], currentPoints[i + 1]);
                total += segDist;
                segments.push({
                    p1: currentPoints[i],
                    p2: currentPoints[i + 1],
                    dist: segDist,
                    midX: (currentPoints[i].x + currentPoints[i + 1].x) / 2,
                    midY: (currentPoints[i].y + currentPoints[i + 1].y) / 2
                });
            }
            const lastPt = currentPoints[currentPoints.length - 1];
            setCompletedMeasurements([
                {
                    id: `m-${Date.now()}`,
                    type: 'ruler',
                    points: [...currentPoints],
                    segments,
                    totalMeters: Math.round(total * 10) / 10,
                    lastPt
                }
            ]);
            setCurrentPoints([]);
        } else if (activeTool === 'area' && currentPoints.length >= 3) {
            const stats = getPolygonAreaMeters(currentPoints);
            setCompletedMeasurements([
                { id: `m-${Date.now()}`, type: 'area', points: [...currentPoints], ...stats }
            ]);
            setCurrentPoints([]);
        } else {
            setCurrentPoints([]);
        }
    }, [activeTool, currentPoints]);

    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === 'Shift') {
                setIsShiftPressed(true);
            }
            if (e.key === 'Escape') {
                e.preventDefault();
                if (currentPoints.length > 0) {
                    setCurrentPoints(prev => prev.slice(0, -1));
                } else {
                    setSelectedCoords(null);
                    setCompletedMeasurements([]);
                }
            }
            if (e.key === 'Enter') {
                e.preventDefault();
                finishMeasurement();
            }
            if (e.key === 'Delete' || e.key === 'Backspace') {
                if (currentPoints.length > 0 || completedMeasurements.length > 0) {
                    e.preventDefault();
                    setCurrentPoints([]);
                    setCompletedMeasurements([]);
                }
            }
        };

        const handleKeyUp = (e) => {
            if (e.key === 'Shift') {
                setIsShiftPressed(false);
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        window.addEventListener('keyup', handleKeyUp);
        return () => {
            window.removeEventListener('keydown', handleKeyDown);
            window.removeEventListener('keyup', handleKeyUp);
        };
    }, [completedMeasurements.length, currentPoints.length, finishMeasurement]);

    const handleCanvasClick = (e) => {
        if (!svgRef.current || !activeTool) return;
        const rect = svgRef.current.getBoundingClientRect();
        const rawX = Math.round(e.clientX - rect.left);
        const rawY = Math.round(e.clientY - rect.top);

        if (activeTool === 'coords') {
            const baseLat = 56.838011;
            const baseLng = 60.597465;
            const lat = (baseLat + (250 - rawY) * 0.00015).toFixed(6);
            const lng = (baseLng + (rawX - 250) * 0.00025).toFixed(6);

            const hitObjects = SAMPLE_MAP_OBJECTS.filter(obj => isPointInPolygon({ x: rawX, y: rawY }, obj.points));

            setSelectedCoords({
                x: rawX,
                y: rawY,
                lat,
                lng,
                objects: hitObjects.map(o => o.name)
            });
            return;
        }

        const snapped = getSnappedCoordinates(rawX, rawY, currentPoints, e.shiftKey || isShiftPressed);
        const { x, y } = snapped;

        // Завершение кликом по последней точке
        if (currentPoints.length >= 2) {
            const last = currentPoints[currentPoints.length - 1];
            if (Math.hypot(last.x - rawX, last.y - rawY) < 14) {
                finishMeasurement();
                return;
            }
        }

        // Замыкание площади кликом по стартовой точке
        if (activeTool === 'area' && currentPoints.length >= 3) {
            const first = currentPoints[0];
            if (Math.hypot(first.x - rawX, first.y - rawY) < 16) {
                finishMeasurement();
                return;
            }
        }

        setCurrentPoints(prev => [...prev, { x, y }]);
    };

    const handleMouseMove = (e) => {
        if (!svgRef.current) return;
        const rect = svgRef.current.getBoundingClientRect();
        const rawX = Math.round(e.clientX - rect.left);
        const rawY = Math.round(e.clientY - rect.top);
        const snapped = getSnappedCoordinates(rawX, rawY, currentPoints, e.shiftKey || isShiftPressed);
        setCursorPos(snapped);
    };

    const handleReset = () => {
        setCurrentPoints([]);
        setCompletedMeasurements([]);
        setSelectedCoords(null);
    };

    const handleCopyCoords = (text) => {
        navigator.clipboard.writeText(text);
        setCopiedToast(true);
        setTimeout(() => setCopiedToast(false), 2000);
    };

    const getReticleColor = () => {
        if (activeTool === 'ruler') return '#ef4444';
        if (activeTool === 'area') return '#8b5cf6';
        if (activeTool === 'coords') return '#2563eb';
        return '#64748b';
    };

    return (
        <SandboxWrapper
            title="Интерактивный тренажёр: Измерения на карте"
            onReset={handleReset}
        >
            <div style={{ display: 'flex', flexDirection: 'column', width: '100%', background: '#ffffff' }}>
                {/* Компактная панель инструментов над холстом без лишних подсказок */}
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '6px 12px',
                    background: '#f8fafc',
                    borderBottom: '1px solid #e2e8f0',
                    gap: '6px'
                }}>
                    <button
                        className={`${styles.sandboxToolBtn} ${activeTool === 'ruler' ? styles.sandboxToolBtnActive : ''}`}
                        onClick={() => handleToolSelect('ruler')}
                        title="Инструмент «Линейка»"
                    >
                        <RulerIcon size={14} />
                        <span>Линейка</span>
                    </button>
                    <button
                        className={`${styles.sandboxToolBtn} ${activeTool === 'area' ? styles.sandboxToolBtnActive : ''}`}
                        onClick={() => handleToolSelect('area')}
                        title="Инструмент «Замер площади»"
                    >
                        <DrawPolygonIcon size={14} />
                        <span>Площадь</span>
                    </button>
                    <button
                        className={`${styles.sandboxToolBtn} ${activeTool === 'coords' ? styles.sandboxToolBtnActive : ''}`}
                        onClick={() => handleToolSelect('coords')}
                        title="Инструмент «Координаты точки»"
                    >
                        <CrosshairIcon size={14} />
                        <span>Координаты</span>
                    </button>
                </div>

                {/* SVG Холст замеров на всю ширину и высоту */}
                <div className={styles.sandboxCanvasArea} style={{ width: '100%', height: '380px', position: 'relative' }}>
                    <svg
                        ref={svgRef}
                        width="100%"
                        height="100%"
                        onClick={handleCanvasClick}
                        onMouseMove={handleMouseMove}
                        onMouseLeave={() => setCursorPos(null)}
                        style={{
                            display: 'block',
                            width: '100%',
                            height: '100%',
                            cursor: activeTool ? 'crosshair' : 'default'
                        }}
                    >
                        <defs>
                            {/* Координатная сетка */}
                            <pattern id="measure-grid" width="30" height="30" patternUnits="userSpaceOnUse">
                                <path d="M 30 0 L 0 0 0 30" fill="none" stroke="#e2e8f0" strokeWidth="0.8" />
                            </pattern>
                        </defs>
                        <rect width="100%" height="100%" fill="url(#measure-grid)" />

                        {/* Дорога (прямой горизонтальный проезд) */}
                        <g>
                            <rect x="0" y="285" width="100%" height="45" fill="#e2e8f0" stroke="#cbd5e1" strokeWidth="1" />
                            <line x1="0" y1="307" x2="100%" y2="307" stroke="#ffffff" strokeWidth="2" strokeDasharray="12 8" />
                            <text x="50" y="303" fill="#64748b" fontSize="10" fontWeight="600" letterSpacing="0.5px">
                                ГЛАВНАЯ ДОРОГА (ширина 22 м)
                            </text>
                        </g>

                        {/* Ровные прямоугольные участки и строения */}
                        {SAMPLE_MAP_OBJECTS.map(obj => {
                            const ptsStr = obj.points.map(p => `${p.x},${p.y}`).join(' ');
                            const minX = Math.min(...obj.points.map(p => p.x));
                            const minY = Math.min(...obj.points.map(p => p.y));

                            return (
                                <g key={obj.id}>
                                    <polygon
                                        points={ptsStr}
                                        fill={obj.fill}
                                        stroke={obj.stroke}
                                        strokeWidth="1.5"
                                    />
                                    <text
                                        x={minX + 8}
                                        y={minY + 16}
                                        fill="#1e293b"
                                        fontSize="11"
                                        fontWeight="600"
                                    >
                                        {obj.name}
                                    </text>
                                    <text
                                        x={minX + 8}
                                        y={minY + 28}
                                        fill="#64748b"
                                        fontSize="9.5"
                                        fontWeight="500"
                                    >
                                        {obj.sublabel}
                                    </text>
                                </g>
                            );
                        })}

                        {/* Завершённые замеры */}
                        {completedMeasurements.map(m => {
                            if (m.type === 'ruler') {
                                const ptsStr = m.points.map(p => `${p.x},${p.y}`).join(' ');
                                return (
                                    <g key={m.id}>
                                        <polyline
                                            points={ptsStr}
                                            stroke="#ef4444"
                                            strokeWidth="2.5"
                                            strokeDasharray="5 3"
                                            fill="none"
                                        />
                                        {m.points.map((p, idx) => (
                                            <circle key={idx} cx={p.x} cy={p.y} r="4.5" fill="#ef4444" stroke="#ffffff" strokeWidth="1.5" />
                                        ))}
                                        {/* Длина каждого отрезка */}
                                        {m.segments.map((seg, idx) => (
                                            <g key={`seg-${idx}`}>
                                                <rect x={seg.midX - 22} y={seg.midY - 10} width="44" height="18" rx="4" fill="#ef4444" />
                                                <text x={seg.midX} y={seg.midY + 3} fill="#ffffff" fontSize="10" fontWeight="bold" textAnchor="middle">
                                                    {seg.dist} м
                                                </text>
                                            </g>
                                        ))}
                                        {/* Итоговая суммарная длина в конце маршрута */}
                                        <g transform={`translate(${m.lastPt.x}, ${m.lastPt.y})`}>
                                            <rect x="8" y="-12" width="92" height="24" rx="4" fill="#991b1b" stroke="#ffffff" strokeWidth="1.5" />
                                            <text x="54" y="4" fill="#ffffff" fontSize="10.5" fontWeight="bold" textAnchor="middle">
                                                Итого: {m.totalMeters} м
                                            </text>
                                        </g>
                                    </g>
                                );
                            } else if (m.type === 'area') {
                                const ptsStr = m.points.map(p => `${p.x},${p.y}`).join(' ');
                                const center = m.points.reduce((acc, p) => ({ x: acc.x + p.x / m.points.length, y: acc.y + p.y / m.points.length }), { x: 0, y: 0 });
                                return (
                                    <g key={m.id}>
                                        <polygon
                                            points={ptsStr}
                                            stroke="#8b5cf6"
                                            strokeWidth="2"
                                            fill="rgba(139, 92, 246, 0.22)"
                                        />
                                        {m.points.map((p, idx) => (
                                            <circle key={idx} cx={p.x} cy={p.y} r="4" fill="#8b5cf6" stroke="#ffffff" strokeWidth="1.5" />
                                        ))}
                                        {/* Длина каждой стороны полигона */}
                                        {m.segments.map((seg, idx) => (
                                            <g key={`area-seg-${idx}`}>
                                                <rect x={seg.midX - 20} y={seg.midY - 8} width="40" height="16" rx="3" fill="#8b5cf6" />
                                                <text x={seg.midX} y={seg.midY + 3.5} fill="#ffffff" fontSize="9.5" fontWeight="bold" textAnchor="middle">
                                                    {seg.dist} м
                                                </text>
                                            </g>
                                        ))}
                                        {/* Центральная плашка: Площадь + Периметр */}
                                        <rect x={center.x - 60} y={center.y - 16} width="120" height="32" rx="5" fill="#6d28d9" stroke="#ffffff" strokeWidth="1.5" />
                                        <text x={center.x} y={center.y - 1} fill="#ffffff" fontSize="11" fontWeight="bold" textAnchor="middle">
                                            {m.sqMeters} м² ({m.sotki} сот.)
                                        </text>
                                        <text x={center.x} y={center.y + 11} fill="#ddd6fe" fontSize="9.5" fontWeight="500" textAnchor="middle">
                                            Периметр: {m.perimeter} м
                                        </text>
                                    </g>
                                );
                            }
                            return null;
                        })}

                        {/* Черновой замер Линейкой в процессе (БЕЗ промежуточного текста) */}
                        {activeTool === 'ruler' && currentPoints.length > 0 && (
                            <g>
                                <polyline
                                    points={currentPoints.map(p => `${p.x},${p.y}`).join(' ')}
                                    stroke="#ef4444"
                                    strokeWidth="2"
                                    fill="none"
                                />
                                {cursorPos && (
                                    <line
                                        x1={currentPoints[currentPoints.length - 1].x}
                                        y1={currentPoints[currentPoints.length - 1].y}
                                        x2={cursorPos.x}
                                        y2={cursorPos.y}
                                        stroke="#ef4444"
                                        strokeWidth="1.5"
                                        strokeDasharray="4 3"
                                    />
                                )}
                                {currentPoints.map((p, idx) => (
                                    <circle key={idx} cx={p.x} cy={p.y} r="4" fill="#ef4444" stroke="#ffffff" strokeWidth="1.5" />
                                ))}
                            </g>
                        )}

                        {/* Черновой замер Площадью в процессе (БЕЗ промежуточного текста) */}
                        {activeTool === 'area' && currentPoints.length > 0 && (
                            <g>
                                <polygon
                                    points={[...currentPoints, cursorPos || currentPoints[0]].map(p => `${p.x},${p.y}`).join(' ')}
                                    stroke="#8b5cf6"
                                    strokeWidth="1.5"
                                    strokeDasharray="3 3"
                                    fill="rgba(139, 92, 246, 0.15)"
                                />
                                {currentPoints.map((p, idx) => (
                                    <circle key={idx} cx={p.x} cy={p.y} r="3.5" fill="#8b5cf6" stroke="#ffffff" strokeWidth="1.5" />
                                ))}
                            </g>
                        )}

                        {/* Интерактивный оптический прицел под курсором */}
                        {cursorPos && activeTool && (
                            <g pointerEvents="none">
                                <circle cx={cursorPos.x} cy={cursorPos.y} r="8" fill="none" stroke={getReticleColor()} strokeWidth="1.2" strokeDasharray="3 2" />
                                <circle cx={cursorPos.x} cy={cursorPos.y} r="2.5" fill="#ffffff" stroke={getReticleColor()} strokeWidth="1.5" />
                                <line x1={cursorPos.x - 14} y1={cursorPos.y} x2={cursorPos.x - 4} y2={cursorPos.y} stroke={getReticleColor()} strokeWidth="1.2" />
                                <line x1={cursorPos.x + 4} y1={cursorPos.y} x2={cursorPos.x + 14} y2={cursorPos.y} stroke={getReticleColor()} strokeWidth="1.2" />
                                <line x1={cursorPos.x} y1={cursorPos.y - 14} x2={cursorPos.x} y2={cursorPos.y - 4} stroke={getReticleColor()} strokeWidth="1.2" />
                                <line x1={cursorPos.x} y1={cursorPos.y + 4} x2={cursorPos.x} y2={cursorPos.y + 14} stroke={getReticleColor()} strokeWidth="1.2" />
                            </g>
                        )}

                        {/* Маркер координаты при клике */}
                        {selectedCoords && (
                            <g>
                                <circle cx={selectedCoords.x} cy={selectedCoords.y} r="16" fill="none" stroke="#2563eb" strokeWidth="2" strokeDasharray="3 3" />
                                <circle cx={selectedCoords.x} cy={selectedCoords.y} r="3.5" fill="#2563eb" />
                                <line x1={selectedCoords.x - 24} y1={selectedCoords.y} x2={selectedCoords.x + 24} y2={selectedCoords.y} stroke="#2563eb" strokeWidth="1.5" />
                                <line x1={selectedCoords.x} y1={selectedCoords.y - 24} x2={selectedCoords.x} y2={selectedCoords.y + 24} stroke="#2563eb" strokeWidth="1.5" />
                            </g>
                        )}
                    </svg>

                    {/* Всплывающая карточка координат при клике (сверху справа) */}
                    {selectedCoords && (
                        <div style={{
                            position: 'absolute',
                            top: '12px',
                            right: '12px',
                            background: 'var(--color-surface, #ffffff)',
                            border: '1px solid var(--color-border, #cbd5e1)',
                            boxShadow: '0 6px 16px rgba(0,0,0,0.12)',
                            borderRadius: '8px',
                            padding: '12px 14px',
                            fontSize: '12px',
                            zIndex: 10,
                            width: '270px'
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                                <strong style={{ color: 'var(--color-primary, #2563eb)' }}>Координаты WGS-84</strong>
                                <button
                                    onClick={() => setSelectedCoords(null)}
                                    style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '13px', color: '#64748b' }}
                                >
                                    ✕
                                </button>
                            </div>
                            <div style={{ fontFamily: 'monospace', background: 'var(--color-surface-hover, #f1f5f9)', padding: '6px 8px', borderRadius: '4px', marginBottom: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', border: '1px solid #e2e8f0' }}>
                                <span style={{ fontSize: '11.5px' }}>{selectedCoords.lat}, {selectedCoords.lng}</span>
                                <button
                                    onClick={() => handleCopyCoords(`${selectedCoords.lat}, ${selectedCoords.lng}`)}
                                    title="Скопировать координаты"
                                    style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', color: '#2563eb' }}
                                >
                                    <CopyIcon size={14} />
                                </button>
                            </div>
                            {copiedToast && <span style={{ color: '#16a34a', fontSize: '11px', display: 'block', marginBottom: '6px', fontWeight: 600 }}>✓ Скопировано в буфер!</span>}
                            <div>
                                <span style={{ color: '#64748b', fontSize: '11px' }}>Объекты в точке:</span>{' '}
                                <strong style={{ display: 'block', marginTop: '2px', color: '#1e293b' }}>
                                    {selectedCoords.objects.length > 0 ? selectedCoords.objects.join(', ') : 'Свободная территория'}
                                </strong>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </SandboxWrapper>
    );
};

export default MeasurementSandbox;
